export type Cell = { x: number; y: number; color: string };

export function cellsToCSV(cells: Cell[]): string {
  const lines = ['x,y,color'];
  for (const c of cells) lines.push(`${c.x},${c.y},${c.color}`);
  return lines.join('\n');
}

export function csvToCells(text: string): Cell[] {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const out: Cell[] = [];
  for (const l of lines) {
    if (l.startsWith('x,y')) continue;
    const parts = l.split(',');
    if (parts.length >= 3) {
      const x = parseInt(parts[0], 10);
      const y = parseInt(parts[1], 10);
      const color = parts.slice(2).join(',');
      if (!isNaN(x) && !isNaN(y)) out.push({ x, y, color });
    }
  }
  return out;
}

export async function exportToImage(
  cells: Cell[],
  cellSize = 20,
  padding = 2,
  bgColor = '#ffffff',
  showNumbers = true,
  scale = 3,
  showColorIndexInCells = false,
  colorIndexMap?: Record<string, number>,
  // optional forced origin and dimensions so callers can render a fixed tile region
  forceMinX?: number,
  forceMinY?: number,
  forceW?: number,
  forceH?: number
): Promise<string> {
  // If no cells and no forced dimensions, emit a small blank canvas
  if (cells.length === 0 && (forceW === undefined || forceH === undefined)) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = cellSize * 4;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0,0,canvas.width,canvas.height);
    return canvas.toDataURL();
  }

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of cells) {
    if (c.x < minX) minX = c.x;
    if (c.y < minY) minY = c.y;
    if (c.x > maxX) maxX = c.x;
    if (c.y > maxY) maxY = c.y;
  }
  // apply forced origin if provided (used for consistent per-tile placement)
  if (forceMinX !== undefined) minX = forceMinX;
  if (forceMinY !== undefined) minY = forceMinY;
  let w = (maxX - minX + 1);
  let h = (maxY - minY + 1);
  if (forceW !== undefined) w = forceW;
  if (forceH !== undefined) h = forceH;
  // Use a minimum exported cell pixel size so exports remain crisp even when
  // the editor is zoomed out. This prevents tiny source cell sizes from being
  // scaled up (which causes visual pixelation) and keeps font sizes proportional.
  const MIN_EXPORT_CELL = 12;
  const drawCellSize = Math.max(cellSize, MIN_EXPORT_CELL);
  const NUMBER_MARGIN = showNumbers ? Math.max(28, Math.round(drawCellSize * 1.6)) : 0;
  const unscaledW = NUMBER_MARGIN + w * drawCellSize + padding * 2;
  const unscaledH = NUMBER_MARGIN + h * drawCellSize + padding * 2;
  const canvas = document.createElement('canvas');
  // Some browsers have maximum canvas size limits (e.g. ~16384 px). If the requested
  // export size would exceed that, reduce the scale to a safe value to avoid a blank/failed export.
  const MAX_CANVAS_SIDE = 16384; // conservative cap; adjust if needed per target browsers
  const maxScaleByWidth = Math.floor(MAX_CANVAS_SIDE / Math.max(1, unscaledW));
  const maxScaleByHeight = Math.floor(MAX_CANVAS_SIDE / Math.max(1, unscaledH));
  const allowedMaxScale = Math.max(1, Math.min(maxScaleByWidth, maxScaleByHeight));
  const effectiveScale = Math.min(Math.max(1, Math.floor(scale)), allowedMaxScale);
  if (effectiveScale !== scale) {
    // inform user that we reduced the scale to fit browser limits
    try{ alert(`Requested export scale ${scale}x was reduced to ${effectiveScale}x to fit browser canvas limits.`); }catch(e){}
  }
  canvas.width = Math.max(1, Math.floor(unscaledW * effectiveScale));
  canvas.height = Math.max(1, Math.floor(unscaledH * effectiveScale));
  const ctx = canvas.getContext('2d');
  if(!ctx) throw new Error('Unable to get 2D canvas context for export');
  // scale drawing so the output is `effectiveScale` times larger
  ctx.scale(effectiveScale, effectiveScale);
  ctx.fillStyle = bgColor || '#ffffff';
  ctx.fillRect(0,0,unscaledW,unscaledH);

  if (showNumbers) {
    // draw header background for numbers
    ctx.fillStyle = '#eeeeee';
    ctx.fillRect(0,0,unscaledW,NUMBER_MARGIN);
    ctx.fillRect(0,0,NUMBER_MARGIN,unscaledH);
    // draw small numbers (smaller font, column numbers rotated)
    ctx.fillStyle = '#000000';
    // choose font size relative to drawCellSize so numbers remain proportional
    // to the exported cell size (not the editor zoom level).
    const fontSize = Math.max(9, Math.round(drawCellSize * 0.45));
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textBaseline = 'middle';

    // compute grid start (including padding) so numbers line up with cell centers
    const gridStartX = NUMBER_MARGIN + padding;
    const gridStartY = NUMBER_MARGIN + padding;

    // column numbers: rotate 90deg (other direction) and center above each column,
    // but pull them a bit closer to the grid (so their baseline sits nearer the header bottom)
    const colPull = Math.round(NUMBER_MARGIN * 0.75);
    for (let c = 0; c < w; c++) {
      const cx = gridStartX + c * drawCellSize + drawCellSize / 2;
      const cy = colPull;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(Math.PI / 2);
      ctx.textAlign = 'center';
      // Use 1-indexed column numbers for export
      ctx.fillText(String(c + 1), 0, 0);
      ctx.restore();
    }

    // row numbers: centered vertically in the margin for each row (use gridStartY)
    // pull row numbers a bit closer to the grid (toward the right)
    const rowPullX = Math.round(NUMBER_MARGIN * 0.75);
    ctx.textAlign = 'center';
    for (let r = 0; r < h; r++) {
      const y = gridStartY + r * drawCellSize + drawCellSize / 2;
      const rx = rowPullX;
      // Use 1-indexed row numbers for export
      ctx.fillText(String(r + 1), rx, y);
    }
  }

  for (const c of cells) {
    const x = NUMBER_MARGIN + (c.x - minX) * drawCellSize + padding;
    const y = NUMBER_MARGIN + (c.y - minY) * drawCellSize + padding;
    ctx.fillStyle = c.color;
    ctx.fillRect(x, y, drawCellSize, drawCellSize);
    // optionally draw small color index inside the cell
    if (showColorIndexInCells && colorIndexMap) {
      const hexKey = (c.color || '').toUpperCase();
      const idx = colorIndexMap[hexKey] ?? colorIndexMap[c.color];
      if (typeof idx === 'number') {
        const fontSizeIdx = Math.max(8, Math.round(drawCellSize * 0.45));
        ctx.font = `${fontSizeIdx}px sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        // position: centered in the cell
        const tx = x + drawCellSize / 2;
        const ty = y + drawCellSize / 2;
        // choose contrasting text color based on cell color luminance
        function hexToRgb(hex: string){ const s = hex.replace('#',''); const v = s.length===3 ? s.split('').map(c=>c+c).join('') : s; const r = parseInt(v.slice(0,2),16); const g = parseInt(v.slice(2,4),16); const b = parseInt(v.slice(4,6),16); return [r,g,b]; }
        const [rr,gg,bb] = (()=>{ try{ return hexToRgb(c.color); }catch(e){ return [255,255,255]; } })();
        const lum = (0.299*rr + 0.587*gg + 0.114*bb) / 255;
        ctx.fillStyle = lum < 0.5 ? '#ffffff' : '#111111';
        ctx.fillText(String(idx), tx, ty);
      }
    }
  }

  // draw grid lines on top so they're visible in the export
  // include the padding offset so lines align exactly with cell edges
  const gridStartX = NUMBER_MARGIN + padding;
  const gridStartY = NUMBER_MARGIN + padding;
  const gridWidth = w * drawCellSize;
  const gridHeight = h * drawCellSize;

  // Set a thin line width that maps to ~1 device pixel after scaling
  ctx.lineWidth = Math.max(0.25, 1 / Math.max(1, effectiveScale));

  // Draw a subtle white stroke then a subtle dark stroke to keep lines visible
  // over both light and dark cells. Use integer-aligned coordinates (no +0.5)
  ctx.strokeStyle = 'rgba(255,255,255,0.22)';
  for (let c = 0; c <= w; c++) {
    const x = gridStartX + c * drawCellSize;
    ctx.beginPath(); ctx.moveTo(x, gridStartY); ctx.lineTo(x, gridStartY + gridHeight); ctx.stroke();
  }
  for (let r = 0; r <= h; r++) {
    const y = gridStartY + r * drawCellSize;
    ctx.beginPath(); ctx.moveTo(gridStartX, y); ctx.lineTo(gridStartX + gridWidth, y); ctx.stroke();
  }

  ctx.strokeStyle = 'rgba(0,0,0,0.18)';
  for (let c = 0; c <= w; c++) {
    const x = gridStartX + c * drawCellSize;
    ctx.beginPath(); ctx.moveTo(x, gridStartY); ctx.lineTo(x, gridStartY + gridHeight); ctx.stroke();
  }
  for (let r = 0; r <= h; r++) {
    const y = gridStartY + r * drawCellSize;
    ctx.beginPath(); ctx.moveTo(gridStartX, y); ctx.lineTo(gridStartX + gridWidth, y); ctx.stroke();
  }

  return canvas.toDataURL();
}
